/**
 * Pure React Query hooks for server data
 *
 * Simple, clean, hobby-friendly approach:
 * - React Query handles all server state management
 * - No complex hybrid patterns
 * - Direct data access with sensible defaults
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../stores/authStore'
import { useUIStore } from '../../stores/uiStore'
import { createAbusePrevention } from '../../utils/rate-limiting'
import { queryKeys, invalidationHelpers } from '../queryKeys'
import type { AppSettings, DashboardSection, WeeklySplit, MuscleGroupOverrides } from '../../types'

// Migration helper to convert old string format to new array format
function migrateWeeklySplit(split: WeeklySplit | Record<string, string | string[]> | null): WeeklySplit {
  if (!split) return {}
  
  const migrated: WeeklySplit = {}
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
  
  // Check if cycle_mode exists in the split object
  if ('cycle_mode' in split && typeof split.cycle_mode === 'boolean') {
    migrated.cycle_mode = split.cycle_mode
  }
  
  for (const day of days) {
    const value = split[day]
    if (value) {
      // If it's already an array, use it
      if (Array.isArray(value)) {
        migrated[day] = value
      } else if (typeof value === 'string') {
        // Convert string to array
        migrated[day] = [value]
      }
    }
  }
  
  return migrated
}

// Default settings
const defaultSettings: AppSettings = {
  theme: 'dark',
  units: 'metric',
  dataSource: 'api',
  algorithms: {
    targetWorkoutsPerWeek: 5,
    bodyweightPlateauThreshold: 0.2,
    exercisePlateauThreshold: 2.0
  }
}

// Server-side user settings interface
export interface UserSettings {
  userSeenSetup: boolean
  enabledSections: DashboardSection[]
  workoutTags: Record<string, string>
  settings: AppSettings
  setsWeekEnabled: boolean
  weeklySplit: WeeklySplit
  routineEnabled: boolean
  muscleGroupOverrides: MuscleGroupOverrides
}

export const defaultUserSettings: UserSettings = {
  userSeenSetup: false,
  enabledSections: ['calendar', 'weight', 'routine', 'weeklyStats', 'muscleGroups'],
  workoutTags: {},
  settings: defaultSettings,
  setsWeekEnabled: false,
  weeklySplit: {},
  routineEnabled: false,
  muscleGroupOverrides: {}
}

// Pure React Query hook for user settings
export function useUserSettings() {
  const { user } = useAuth()
  const setTheme = useUIStore((state) => state.setTheme)
  const hasInitializedTheme = useRef(false)

  return useQuery({
    queryKey: queryKeys.userSettings(user?.id || ''),
    queryFn: async (): Promise<UserSettings> => {
      if (!user) {
        throw new Error('No authenticated user')
      }

      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error('Failed to fetch user settings:', error)
        throw error
      }

      // Always return complete settings object with defaults
      const settings = {
        userSeenSetup: data?.user_seen_setup ?? false,
        enabledSections: data?.enabled_sections || defaultUserSettings.enabledSections,
        workoutTags: data?.workout_tags || {},
        settings: { ...defaultSettings, ...data?.settings },
        setsWeekEnabled: data?.sets_week_enabled ?? false,
        weeklySplit: migrateWeeklySplit(data?.weekly_split || {}),
        routineEnabled: data?.routine_enabled ?? false,
        muscleGroupOverrides: data?.muscle_group_overrides || {}
      }

      // Sync theme from DB to UI store on initial load only
      // This ensures the UI theme matches the saved server preference
      // but doesn't interfere with pending theme changes in settings
      if (!hasInitializedTheme.current) {
        const currentUITheme = useUIStore.getState().theme
        if (settings.settings.theme !== currentUITheme) {
          setTheme(settings.settings.theme)
        }
        hasInitializedTheme.current = true
      }

      return settings
    },
    enabled: !!user
  })
}

// Simple mutation hooks with invisible abuse prevention
const settingsRateLimit = createAbusePrevention('user_settings', 20, 60000) // 20 saves per minute max

export function useUpdateUserSettings() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (updates: Partial<UserSettings>): Promise<void> => {
      if (!user) throw new Error('No authenticated user')

      // Invisible abuse prevention - only blocks obvious spam (20+ saves/minute)
      if (!settingsRateLimit.canProceed()) {
        throw new Error('Settings update rate exceeded. Please wait a moment.')
      }

      // Simple database update with explicit timestamps
      const dbUpdates: Record<string, unknown> = {
        user_id: user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...updates
      }

      // Convert camelCase to snake_case for database
      if (updates.userSeenSetup !== undefined) {
        dbUpdates.user_seen_setup = updates.userSeenSetup
        delete dbUpdates.userSeenSetup
      }
      if (updates.enabledSections !== undefined) {
        dbUpdates.enabled_sections = updates.enabledSections
        delete dbUpdates.enabledSections
      }
      if (updates.workoutTags !== undefined) {
        dbUpdates.workout_tags = updates.workoutTags
        delete dbUpdates.workoutTags
      }
      if (updates.setsWeekEnabled !== undefined) {
        dbUpdates.sets_week_enabled = updates.setsWeekEnabled
        delete dbUpdates.setsWeekEnabled
      }
      if (updates.weeklySplit !== undefined) {
        dbUpdates.weekly_split = updates.weeklySplit
        delete dbUpdates.weeklySplit
      }
      if (updates.routineEnabled !== undefined) {
        dbUpdates.routine_enabled = updates.routineEnabled
        delete dbUpdates.routineEnabled
      }
      if (updates.muscleGroupOverrides !== undefined) {
        dbUpdates.muscle_group_overrides = updates.muscleGroupOverrides
        delete dbUpdates.muscleGroupOverrides
      }

      const { error } = await supabase.from('user_settings').upsert(dbUpdates)

      if (error) {
        console.error('Failed to update user settings:', error)
        throw error
      }
    },
    onMutate: async (updates) => {
      const settingsKey = queryKeys.userSettings(user?.id || '')

      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: settingsKey })

      // Snapshot the current value
      const previousSettings = queryClient.getQueryData<UserSettings>(settingsKey)

      // Optimistically update the cache
      queryClient.setQueryData(settingsKey, (old: UserSettings | undefined) => ({
        ...(old || defaultUserSettings),
        ...updates
      }))

      // Return rollback data
      return { previousSettings }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData(queryKeys.userSettings(user?.id || ''), context.previousSettings)
      }
      console.error('Failed to save settings:', error)
      toast.error('Failed to save settings')

      // Only refetch on error to get the correct state
      queryClient.invalidateQueries({ queryKey: queryKeys.userSettings(user?.id || '') })
    }
    // No onSettled - don't refetch on success, optimistic update is enough
  })
}

// Utility to ensure user settings exist (for new users)
export function useEnsureUserSettings() {
  const { user } = useAuth()
  const updateSettings = useUpdateUserSettings()

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('No authenticated user')

      // This will create settings with defaults if they don't exist
      await updateSettings.mutateAsync({})
    }
  })
}

// Workout Tags Mutations
export function useSetWorkoutTag() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ workoutId, tag }: { workoutId: string; tag: string }): Promise<void> => {
      if (!user) throw new Error('No authenticated user')

      // Get current workout tags
      const currentSettings = queryClient.getQueryData<UserSettings>(
        queryKeys.userSettings(user.id)
      )
      const currentTags = currentSettings?.workoutTags || {}

      // Update with new tag
      const updatedTags = { ...currentTags, [workoutId]: tag }

      // Database update
      const dbUpdates = {
        user_id: user.id,
        workout_tags: updatedTags,
        updated_at: new Date().toISOString()
      }

      const { error } = await supabase.from('user_settings').upsert(dbUpdates)

      if (error) {
        console.error('Failed to set workout tag:', error)
        throw error
      }
    },
    onMutate: async ({ workoutId, tag }) => {
      const settingsKey = queryKeys.userSettings(user?.id || '')

      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: settingsKey })

      // Snapshot the current value
      const previousSettings = queryClient.getQueryData<UserSettings>(settingsKey)

      // Optimistically update the cache
      queryClient.setQueryData(settingsKey, (old: UserSettings | undefined) => {
        const currentTags = old?.workoutTags || {}
        return {
          ...(old || defaultUserSettings),
          workoutTags: { ...currentTags, [workoutId]: tag }
        }
      })

      // Return rollback data
      return { previousSettings }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData(queryKeys.userSettings(user?.id || ''), context.previousSettings)
      }
      console.error('Failed to set workout tag:', error)
    }
  })
}

export function useRemoveWorkoutTag() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (workoutId: string): Promise<void> => {
      if (!user) throw new Error('No authenticated user')

      // Get current workout tags
      const currentSettings = queryClient.getQueryData<UserSettings>(
        queryKeys.userSettings(user.id)
      )
      const currentTags = currentSettings?.workoutTags || {}

      // Remove the tag
      const { [workoutId]: _removed, ...updatedTags } = currentTags

      // Database update
      const dbUpdates = {
        user_id: user.id,
        workout_tags: updatedTags,
        updated_at: new Date().toISOString()
      }

      const { error } = await supabase.from('user_settings').upsert(dbUpdates)

      if (error) {
        console.error('Failed to remove workout tag:', error)
        throw error
      }
    },
    onMutate: async (workoutId) => {
      const settingsKey = queryKeys.userSettings(user?.id || '')

      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: settingsKey })

      // Snapshot the current value
      const previousSettings = queryClient.getQueryData<UserSettings>(settingsKey)

      // Optimistically update the cache
      queryClient.setQueryData(settingsKey, (old: UserSettings | undefined) => {
        const currentTags = old?.workoutTags || {}
        const { [workoutId]: _removed, ...updatedTags } = currentTags
        return {
          ...(old || defaultUserSettings),
          workoutTags: updatedTags
        }
      })

      // Return rollback data
      return { previousSettings }
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousSettings) {
        queryClient.setQueryData(queryKeys.userSettings(user?.id || ''), context.previousSettings)
      }
      console.error('Failed to remove workout tag:', error)
    }
  })
}

export function useUpdateAppSettings() {
  const { user } = useAuth()
  const updateSettings = useUpdateUserSettings()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (settingsUpdate: Partial<AppSettings>) => {
      // Merge with current settings to ensure complete object
      const currentSettings = queryClient.getQueryData(
        queryKeys.userSettings(user?.id || '')
      ) as UserSettings
      const updatedSettings = {
        ...defaultSettings,
        ...currentSettings?.settings,
        ...settingsUpdate
      }
      return updateSettings.mutateAsync({ settings: updatedSettings })
    },
    onSuccess: () => {
      // Toast removed - handled by caller (SettingsPage)
    }
  })
}

// Simplified section management with seamless optimistic updates
export function useUpdateEnabledSections() {
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (enabledSections: DashboardSection[]): Promise<void> => {
      if (!user) throw new Error('No authenticated user')

      const dbUpdates: Record<string, unknown> = {
        user_id: user.id,
        enabled_sections: enabledSections,
        updated_at: new Date().toISOString()
      }

      const { error } = await supabase.from('user_settings').upsert(dbUpdates)

      if (error) {
        console.error('Failed to update enabled sections:', error)
        throw error
      }
    }
    // Note: onMutate, onSuccess, onError are now handled by the calling hook (useSectionReorder)
    // This keeps the mutation flexible for different use cases
  })
}

// Muscle Group Overrides mutation
export function useUpdateMuscleGroupOverrides() {
  const { user: _user } = useAuth()
  const updateSettings = useUpdateUserSettings()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (overrides: MuscleGroupOverrides) => {
      return updateSettings.mutateAsync({ muscleGroupOverrides: overrides })
    },
    onSuccess: () => {
      toast.success('Muscle group overrides saved')
      // Invalidate all workout and routine data since muscle group classification affects all analysis
      queryClient.invalidateQueries({ queryKey: invalidationHelpers.allWorkouts() })
      // Invalidate all routine queries
      queryClient.invalidateQueries({ predicate: (query) => 
        query.queryKey.some(key => typeof key === 'string' && key.includes('routines'))
      })
      // Invalidate any other cached data that depends on muscle group classification
      queryClient.invalidateQueries({ predicate: (query) => 
        query.queryKey.some(key => 
          typeof key === 'string' && (
            key.includes('muscle-groups') ||
            key.includes('analysis') ||
            key.includes('stats')
          )
        )
      })
    },
    onError: (error) => {
      console.error('Failed to save muscle group overrides:', error)
      toast.error('Failed to save muscle group overrides')
    }
  })
}
